import {
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@simplicity/ui";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { apiFetch } from "../../api";
import type { Project, TransformDefinition } from "../../configTypes";
import { LAYER_OPTIONS } from "../../configTypes";
import { Breadcrumbs } from "./Breadcrumbs";
import { ParamsEditor } from "./ParamsEditor";
import { SqlTextarea } from "./SqlTextarea";

/** Identity row: name, layer selector, enabled checkbox. */
function IdentityRow({
  name,
  setName,
  layer,
  setLayer,
  enabled,
  setEnabled,
}: {
  name: string;
  setName: (v: string) => void;
  layer: string;
  setLayer: (v: string) => void;
  enabled: boolean;
  setEnabled: (v: boolean) => void;
}) {
  return (
    <div className="mb-4 grid grid-cols-[1fr_160px_auto] gap-4">
      <div>
        <Label htmlFor="td-name" className="form-label">
          Name
        </Label>
        <Input
          id="td-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. incidents_latest"
        />
      </div>
      <div>
        <Label htmlFor="td-layer" className="form-label">
          Layer
        </Label>
        <Select value={layer} onValueChange={setLayer}>
          <SelectTrigger id="td-layer">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LAYER_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-end pb-1">
        <Label className="flex items-center gap-2 text-sm text-foreground">
          <Checkbox checked={enabled} onCheckedChange={(v) => setEnabled(v === true)} />
          Enabled
        </Label>
      </div>
    </div>
  );
}

export function CreateTransformDefinition() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId") ?? "";

  const [projectName, setProjectName] = useState("");
  const [name, setName] = useState("");
  const [layer, setLayer] = useState<string>(LAYER_OPTIONS[0].value);
  const [sqlTemplate, setSqlTemplate] = useState("");
  const [params, setParams] = useState<Record<string, string>>({});
  const [enabled, setEnabled] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    void apiFetch<Project>(`/api/projects/${projectId}`).then((p) => setProjectName(p.name));
  }, [projectId]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !sqlTemplate.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const td = await apiFetch<TransformDefinition>("/api/transform-definitions", {
        method: "POST",
        body: JSON.stringify({ projectId, name: name.trim(), layer, sqlTemplate, params, enabled }),
      });
      void navigate(`/config/transform-definitions/${td.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create transform definition");
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-4xl">
      <Breadcrumbs
        items={[
          { label: "Projects", to: "/config/projects" },
          { label: projectName || "...", to: `/config/projects/${projectId}` },
          { label: "New Transform" },
        ]}
      />

      <div className="form-card">
        <h2 className="mb-4 text-lg text-foreground">New Transform Definition</h2>
        {error && (
          <div className="mb-4 rounded bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        <form onSubmit={(e) => void handleSubmit(e)}>
          <IdentityRow
            name={name}
            setName={setName}
            layer={layer}
            setLayer={setLayer}
            enabled={enabled}
            setEnabled={setEnabled}
          />

          <div className="mb-4">
            <Label htmlFor="td-sql" className="form-label">
              SQL Template
            </Label>
            <SqlTextarea id="td-sql" value={sqlTemplate} onChange={setSqlTemplate} />
          </div>

          <div className="mb-6 rounded-md border border-border/50 bg-muted/10 p-4">
            <h3 className="mb-3 text-sm font-medium text-muted-foreground">
              Auto-detected transform variables
            </h3>
            <div className="mb-4">
              <span className="form-label">Parameters</span>
              <ParamsEditor params={params} onChange={setParams} sqlTemplate={sqlTemplate} />
            </div>
            <div className="rounded border border-border px-3 py-2 text-sm italic text-muted-foreground">
              Output target and dependencies will also be auto-detected from your SQL on save.
            </div>
          </div>

          <div className="form-actions">
            <Button
              type="button"
              variant="outline"
              onClick={() => void navigate(`/config/projects/${projectId}`)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !name.trim() || !sqlTemplate.trim()}>
              {submitting ? "Creating..." : "Create"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
